import type { DovolenaRequest, DovolenaRequestType } from '../types'
import { comparePersonNames } from '../utils/personNameSort'

export type DovolenaSortKey = 'date_from' | 'date_to' | 'created_at' | 'requester_name' | 'status'

export type DovolenaListFilterParams = {
  nameQuery?: string
  createdFrom?: string
  createdTo?: string
  leaveFrom?: string
  leaveTo?: string
  sortKey?: DovolenaSortKey
  sortDir?: 'asc' | 'desc'
  typeFilter?: 'all' | DovolenaRequestType
  statusFilter?: 'all' | 'new' | 'approved' | 'rejected'
  processedFilter?: 'all' | 'processed' | 'unprocessed'
  personalistaView?: boolean
}

function normalizePersonSearch(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

/** Hledání podle jména / příjmení (formát „Příjmení, Jméno“ i celý řetězec). */
export function matchesDovolenaNameFilter(
  requesterName: string | null | undefined,
  queryRaw: string,
): boolean {
  const q = normalizePersonSearch(queryRaw)
  if (!q) return true
  const name = (requesterName || '').trim()
  if (!name) return false
  const hay = normalizePersonSearch(name)
  if (hay.includes(q)) return true
  const parts = name.split(',').map((p) => p.trim()).filter(Boolean)
  for (const part of parts) {
    if (normalizePersonSearch(part).includes(q)) return true
  }
  return false
}

function dateOnlyMs(value: string | null | undefined): number {
  if (!value) return Number.NaN
  const day = String(value).slice(0, 10)
  const t = Date.parse(`${day}T00:00:00`)
  return Number.isFinite(t) ? t : Number.NaN
}

function inDateRange(value: string | null | undefined, from?: string, to?: string): boolean {
  if (!from && !to) return true
  const ms = dateOnlyMs(value)
  if (!Number.isFinite(ms)) return false
  if (from) {
    const f = dateOnlyMs(from)
    if (Number.isFinite(f) && ms < f) return false
  }
  if (to) {
    const t = dateOnlyMs(to)
    if (Number.isFinite(t) && ms > t) return false
  }
  return true
}

export function filterAndSortDovolenaRequests(
  rows: DovolenaRequest[],
  params: DovolenaListFilterParams,
): DovolenaRequest[] {
  const {
    nameQuery = '',
    createdFrom = '',
    createdTo = '',
    leaveFrom = '',
    leaveTo = '',
    sortKey = 'date_from',
    sortDir = 'desc',
    typeFilter = 'all',
    statusFilter = 'all',
    processedFilter = 'all',
    personalistaView = false,
  } = params

  const q = nameQuery.trim()
  let list = [...rows]

  if (q) {
    list = list.filter(
      (x) =>
        matchesDovolenaNameFilter(x.requester_name, q) ||
        String(x.personal_number || '')
          .toLowerCase()
          .includes(q.toLowerCase()) ||
        (x.created_by_name && matchesDovolenaNameFilter(x.created_by_name, q)),
    )
  }

  if (createdFrom || createdTo) {
    list = list.filter((x) => inDateRange(x.created_at, createdFrom, createdTo))
  }
  if (leaveFrom || leaveTo) {
    list = list.filter((x) => inDateRange(x.date_from, leaveFrom, leaveTo))
  }

  if (typeFilter !== 'all') {
    list = list.filter((x) => String(x.request_type || 'dovolena') === typeFilter)
  }
  if (statusFilter !== 'all') {
    list = list.filter((x) => x.status === statusFilter)
  }
  if (personalistaView && processedFilter !== 'all') {
    list =
      processedFilter === 'processed'
        ? list.filter((x) => Boolean(x.personalista_processed_at))
        : list.filter((x) => !x.personalista_processed_at)
  }

  list.sort((a, b) => {
    let cmp = 0
    switch (sortKey) {
      case 'date_from':
        cmp = dateOnlyMs(a.date_from) - dateOnlyMs(b.date_from)
        break
      case 'date_to':
        cmp = dateOnlyMs(a.date_to) - dateOnlyMs(b.date_to)
        break
      case 'created_at':
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        break
      case 'requester_name':
        cmp = comparePersonNames({ name: a.requester_name }, { name: b.requester_name })
        break
      case 'status':
        cmp = (a.status || '').localeCompare(b.status || '', 'cs')
        break
      default:
        cmp = 0
    }
    if (!Number.isFinite(cmp)) cmp = 0
    return sortDir === 'desc' ? -cmp : cmp
  })

  return list
}
